/**
 * SearchableSelect — drop-in replacement for `<select>` with a built-in
 * filter input. Used across admin pages where the staff / supplier /
 * geofence / department list is long enough that scrolling is painful
 * (Point #8 — Feb 22, 2026 global type-and-search ask).
 *
 * Usage:
 *   <SearchableSelect
 *     value={geoId}
 *     onChange={setGeoId}
 *     options={[{ value: '', label: 'All Geofences' }, ...rows.map(g => ({value: g.id, label: g.name}))]}
 *     placeholder="All Geofences"
 *     testid="ops-filter-geo"
 *   />
 *
 * Props:
 *   value        - current selected value (string)
 *   onChange(v)  - called with the new value
 *   options      - [{ value, label, hint? }, ...]  (hint surfaces under label)
 *   placeholder  - shown when nothing selected (default: "Select…")
 *   disabled     - boolean
 *   testid       - data-testid root (button gets this; menu items get
 *                  `${testid}-opt-${value}`)
 *   className    - extra classes on the trigger button
 *   menuMaxH     - max-height for the dropdown panel (default: 320)
 *   minWidth     - min width in px for the trigger (default: 180)
 *
 * Keyboard:
 *   Enter to open + select first match · Escape to close · Up/Down navigate.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  testid,
  className = '',
  menuMaxH = 320,
  minWidth = 180,
  allowClear = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);          // highlighted option index
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.hint || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, options]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setHi(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const pick = useCallback((opt) => {
    onChange(opt.value);
    setOpen(false);
  }, [onChange]);

  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { setHi((h) => Math.min(h + 1, filtered.length - 1)); e.preventDefault(); }
    if (e.key === 'ArrowUp')   { setHi((h) => Math.max(h - 1, 0)); e.preventDefault(); }
    if (e.key === 'Enter') {
      const opt = filtered[hi];
      if (opt) pick(opt);
      e.preventDefault();
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block" style={{ minWidth }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full inline-flex items-center justify-between gap-2 px-3 py-1.5 border rounded text-sm bg-white text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300 ${className}`}
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && selected && (
            <X
              size={14}
              className="text-gray-400 hover:text-gray-700"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              data-testid={testid ? `${testid}-clear` : undefined}
            />
          )}
          <ChevronDown size={14} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden"
          style={{ minWidth }}
          data-testid={testid ? `${testid}-menu` : undefined}
        >
          <div className="px-2 py-1.5 border-b bg-gray-50 flex items-center gap-1.5">
            <Search size={13} className="text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHi(0); }}
              onKeyDown={onKey}
              placeholder="Type to filter…"
              className="w-full bg-transparent text-sm focus:outline-none placeholder:text-gray-400"
              data-testid={testid ? `${testid}-search` : undefined}
            />
            {query && (
              <X
                size={13}
                className="text-gray-400 cursor-pointer hover:text-gray-700"
                onClick={() => setQuery('')}
              />
            )}
          </div>
          <ul
            role="listbox"
            className="overflow-y-auto py-1 divide-y divide-gray-50"
            style={{ maxHeight: menuMaxH }}
          >
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-xs text-gray-500 text-center">No matches</li>
            )}
            {filtered.map((o, i) => {
              const isActive = String(o.value) === String(value);
              const isHi = i === hi;
              return (
                <li
                  key={o.value === '' ? `__empty__${i}` : o.value}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => pick(o)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                    isHi ? 'bg-blue-50' : 'bg-white'
                  } ${isActive ? 'font-semibold text-blue-700' : 'text-gray-800'}`}
                  data-testid={testid ? `${testid}-opt-${o.value || 'empty'}` : undefined}
                  role="option"
                  aria-selected={isActive}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {o.hint && <span className="block text-[11px] text-gray-500 truncate">{o.hint}</span>}
                  </span>
                  {isActive && <Check size={14} className="text-blue-600 shrink-0" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
